import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Music, Plus, Trash2, ChevronRight, Search } from "lucide-react";

export default function Songs() {
  const [songs, setSongs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", isrc: "", iswc: "", notes: "" });
  const navigate = useNavigate();

  const load = () => api.get("/songs").then(({ data }) => setSongs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      const { data } = await api.post("/songs", { ...form, writer_splits: [], publisher_splits: [] });
      toast.success("Song created");
      setOpen(false);
      setForm({ title: "", isrc: "", iswc: "", notes: "" });
      navigate(`/songs/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/songs/${id}`);
    toast.success("Song deleted");
    load();
  };

  const filtered = songs.filter((s) =>
    [s.title, s.isrc, s.iswc].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="songs-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Catalog</h4>
          <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Songs</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-song-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" /> Add Song
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Add Song</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Title</Label>
                <Input data-testid="song-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-secondary border-border" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">ISRC</Label>
                  <Input data-testid="song-isrc-input" value={form.isrc} onChange={(e) => setForm({ ...form, isrc: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" placeholder="US-XXX-YY-NNNNN" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">ISWC</Label>
                  <Input data-testid="song-iswc-input" value={form.iswc} onChange={(e) => setForm({ ...form, iswc: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" placeholder="T-000000000-0" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="save-song-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Create & Edit Splits</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <Input data-testid="song-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, ISRC, ISWC..." className="pl-9 bg-card border-border" />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Music className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No songs found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Title</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">ISRC</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">ISWC</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Splits</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`song-row-${s.id}`} onClick={() => navigate(`/songs/${s.id}`)} className="border-border cursor-pointer row-in hover:bg-secondary/40">
                  <TableCell className="font-semibold">{s.title}</TableCell>
                  <TableCell className="font-mono text-zinc-300">{s.isrc || "—"}</TableCell>
                  <TableCell className="font-mono text-zinc-300">{s.iswc || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{(s.writer_splits?.length || 0) + (s.publisher_splits?.length || 0)}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={(e) => remove(e, s.id)} data-testid={`delete-song-${s.id}`} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
                    <ChevronRight className="h-4 w-4 text-zinc-600 inline ml-1" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
